import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './CakeProducts.css';

const CakeProducts = () => {
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState('all');
  const [search, setSearch] = useState('');
  const [cakes, setCakes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [editingId, setEditingId] = useState(null);
  const [moreEditId, setMoreEditId] = useState(null);
  const [savingId, setSavingId] = useState(null);
  const [rowMessage, setRowMessage] = useState({ id: null, type: '', text: '' });

  const [quickEdit, setQuickEdit] = useState({
    name: '',
    starting_price: '',
    availability: 'available',
  });

  const [moreEdit, setMoreEdit] = useState({
    category: '',
    description: '',
    egg_type: 'eggless',
    shape: '',
    cake_message_enabled: true,
    customization_enabled: false,
  });

  const [editVariants, setEditVariants] = useState([]);
  const [editFlavors, setEditFlavors] = useState([]);
  const [editImageFile, setEditImageFile] = useState(null);
  const [editImagePreview, setEditImagePreview] = useState('');
  const [previewImage, setPreviewImage] = useState('');

  const [shopCategories, setShopCategories] = useState([]);
  const [shopItems, setShopItems] = useState([]);
  const [shopLoading, setShopLoading] = useState(false);
  const [shopError, setShopError] = useState('');
  const [showAddItem, setShowAddItem] = useState(false);
  const [showAddCategory, setShowAddCategory] = useState(false);
  const [categoryName, setCategoryName] = useState('');
  const [categorySaving, setCategorySaving] = useState(false);
  const [categoryEditingId, setCategoryEditingId] = useState(null);
  const [categoryEditingName, setCategoryEditingName] = useState('');
  const [itemSaving, setItemSaving] = useState(false);
  const [editingShopItemId, setEditingShopItemId] = useState(null);
  const [itemImageFile, setItemImageFile] = useState(null);
  const [itemImagePreview, setItemImagePreview] = useState('');
  const [itemMessage, setItemMessage] = useState({ type: '', text: '' });
  const [itemForm, setItemForm] = useState({
    name: '',
    category_id: '',
    description: '',
    price: '',
    mrp: '',
    unit: '',
    stock_quantity: '',
    availability: 'available',
  });

  let vendor = {};

  try {
    vendor = JSON.parse(localStorage.getItem('cakeVendor')) || {};
  } catch (error) {
    vendor = {};
  }

  const categories = [
    'Birthday Cake',
    'Anniversary Cake',
    'Photo Cake',
    'Designer Cake',
    'Wedding Cake',
    'Custom Cake',
    'Kids Cake',
    'Other',
  ];

  const shapes = [
    { value: '', label: 'Select Shape' },
    { value: 'round', label: 'Round' },
    { value: 'heart', label: 'Heart' },
    { value: 'square', label: 'Square' },
    { value: 'rectangle', label: 'Rectangle' },
    { value: 'custom', label: 'Custom' },
  ];

  const token = () => localStorage.getItem('cakeToken');

  const getCakeImage = (image) => {
    if (!image) return '';

    if (
      image.startsWith('http://') ||
      image.startsWith('https://') ||
      image.startsWith('data:') ||
      image.startsWith('blob:')
    ) {
      return image;
    }

    if (image.includes('/uploads/cakes/')) {
      const filename = image.split('/').pop();
      return `/api/cake/products/image/${encodeURIComponent(filename)}`;
    }

    return image.startsWith('/') ? image : `/${image}`;
  };

  const getShopItemImage = (image) => {
    if (!image) return '';

    if (
      image.startsWith('http://') ||
      image.startsWith('https://') ||
      image.startsWith('data:') ||
      image.startsWith('blob:')
    ) {
      return image;
    }

    if (image.includes('/uploads/cake-shop-items/')) {
      const filename = image.split('/').pop();
      return `/api/cake/products/shop-item-image/${encodeURIComponent(filename)}`;
    }

    return image.startsWith('/') ? image : `/${image}`;
  };

  const loadShopData = async (showLoader = true) => {
    try {
      if (showLoader) setShopLoading(true);
      setShopError('');

      const cakeToken = token();

      if (!cakeToken) {
        navigate('/cake/login');
        return;
      }

      const [categoryResponse, itemResponse] = await Promise.all([
        fetch('/api/cake/products/shop-categories', {
          headers: {
            Authorization: `Bearer ${cakeToken}`,
          },
        }),
        fetch('/api/cake/products/shop-items', {
          headers: {
            Authorization: `Bearer ${cakeToken}`,
          },
        }),
      ]);

      const categoryData = await categoryResponse.json();
      const itemData = await itemResponse.json();

      if (!categoryResponse.ok) {
        throw new Error(categoryData.message || 'Failed to load categories');
      }

      if (!itemResponse.ok) {
        throw new Error(itemData.message || 'Failed to load shop items');
      }

      setShopCategories(
        Array.isArray(categoryData.categories) ? categoryData.categories : []
      );

      setShopItems(
        Array.isArray(itemData.items) ? itemData.items : []
      );
    } catch (error) {
      console.error('Load shop data error:', error);
      setShopError(error.message || 'Failed to load shop items');
    } finally {
      if (showLoader) setShopLoading(false);
    }
  };

  const loadCakes = async (showLoader = true) => {
    try {
      if (showLoader) setLoading(true);
      setError('');

      const cakeToken = token();

      if (!cakeToken) {
        navigate('/cake/login');
        return;
      }

      const response = await fetch('/api/cake/products', {
        headers: {
          Authorization: `Bearer ${cakeToken}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to load cakes');
      }

      setCakes(Array.isArray(data.products) ? data.products : []);
    } catch (error) {
      console.error('Load cakes error:', error);
      setError(error.message || 'Failed to load cakes');
    } finally {
      if (showLoader) setLoading(false);
    }
  };

  useEffect(() => {
    loadCakes();
    loadShopData();
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('cakeToken');
    localStorage.removeItem('cakeVendor');
    navigate('/cake/login');
  };

  const filteredCakes = useMemo(() => {
    let result = [...cakes];

    if (activeTab === 'offers') {
      result = result.filter((cake) => cake.offer);
    }

    if (search.trim()) {
      const text = search.trim().toLowerCase();
      result = result.filter(
        (cake) =>
          cake.name?.toLowerCase().includes(text) ||
          cake.category?.toLowerCase().includes(text)
      );
    }

    return result;
  }, [cakes, activeTab, search]);

  const filteredShopItems = useMemo(() => {
    let result = [...shopItems];

    if (search.trim()) {
      const text = search.trim().toLowerCase();

      result = result.filter(
        (item) =>
          item.name?.toLowerCase().includes(text) ||
          item.category_name?.toLowerCase().includes(text) ||
          item.unit?.toLowerCase().includes(text)
      );
    }

    return result;
  }, [shopItems, search]);

  const resetItemForm = () => {
    setItemForm({
      name: '',
      category_id: '',
      description: '',
      price: '',
      mrp: '',
      unit: '',
      stock_quantity: '',
      availability: 'available',
    });
    setItemImageFile(null);
    setItemImagePreview('');
    setEditingShopItemId(null);
    setItemMessage({ type: '', text: '' });
  };

  const openAddItem = () => {
    resetItemForm();
    setActiveTab('other');
    setShowAddItem(true);
  };

  const closeItemForm = () => {
    resetItemForm();
    setShowAddItem(false);
  };

  const handleItemChange = (e) => {
    const { name, value } = e.target;

    setItemForm((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (itemMessage.text) {
      setItemMessage({ type: '', text: '' });
    }
  };

  const handleItemImage = (e) => {
    const file = e.target.files?.[0];

    if (!file) return;

    if (
      ![
        'image/jpeg',
        'image/png',
        'image/webp',
      ].includes(file.type)
    ) {
      setItemMessage({
        type: 'error',
        text: 'Please select JPG, PNG or WEBP image',
      });
      e.target.value = '';
      return;
    }

    if (file.size > 3 * 1024 * 1024) {
      setItemMessage({
        type: 'error',
        text: 'Image must be under 3 MB',
      });
      e.target.value = '';
      return;
    }

    setItemImageFile(file);
    setItemImagePreview(URL.createObjectURL(file));
    setItemMessage({ type: '', text: '' });
  };

  const saveCategory = async () => {
    const cleanName = categoryName.trim();

    if (!cleanName) {
      return;
    }

    try {
      setCategorySaving(true);

      const response = await fetch('/api/cake/products/shop-categories', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          category_name: cleanName,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to add category');
      }

      setCategoryName('');
      setShowAddCategory(false);
      await loadShopData(false);

      if (data.category?.id) {
        setItemForm((prev) => ({
          ...prev,
          category_id: String(data.category.id),
        }));
      }
    } catch (error) {
      setItemMessage({
        type: 'error',
        text: error.message || 'Failed to add category',
      });
    } finally {
      setCategorySaving(false);
    }
  };

  const startCategoryEdit = (category) => {
    setCategoryEditingId(category.id);
    setCategoryEditingName(category.category_name || '');
  };

  const cancelCategoryEdit = () => {
    setCategoryEditingId(null);
    setCategoryEditingName('');
  };

  const saveCategoryEdit = async (categoryId) => {
    const cleanName = categoryEditingName.trim();

    if (!cleanName) return;

    try {
      setCategorySaving(true);

      const response = await fetch(
        `/api/cake/products/shop-categories/${categoryId}`,
        {
          method: 'PUT',
          headers: {
            Authorization: `Bearer ${token()}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            category_name: cleanName,
          }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to update category');
      }

      cancelCategoryEdit();
      await loadShopData(false);
    } catch (error) {
      setItemMessage({
        type: 'error',
        text: error.message || 'Failed to update category',
      });
    } finally {
      setCategorySaving(false);
    }
  };

  const toggleCategoryStatus = async (category) => {
    const nextStatus =
      category.status === 'active'
        ? 'inactive'
        : 'active';

    try {
      const response = await fetch(
        `/api/cake/products/shop-categories/${category.id}/status`,
        {
          method: 'PATCH',
          headers: {
            Authorization: `Bearer ${token()}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            status: nextStatus,
          }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to update category status');
      }

      await loadShopData(false);
    } catch (error) {
      setItemMessage({
        type: 'error',
        text: error.message || 'Failed to update category status',
      });
    }
  };

  const saveShopItem = async (e) => {
    e.preventDefault();

    if (!itemForm.name.trim()) {
      setItemMessage({
        type: 'error',
        text: 'Product name is required',
      });
      return;
    }

    if (!itemForm.category_id) {
      setItemMessage({
        type: 'error',
        text: 'Please select or add a category',
      });
      return;
    }

    if (!itemForm.price || Number(itemForm.price) <= 0) {
      setItemMessage({
        type: 'error',
        text: 'Valid price is required',
      });
      return;
    }

    try {
      setItemSaving(true);
      setItemMessage({ type: '', text: '' });

      const payload = new FormData();
      payload.append('name', itemForm.name.trim());
      payload.append('category_id', itemForm.category_id);
      payload.append('description', itemForm.description.trim());
      payload.append('price', String(Number(itemForm.price)));
      payload.append('mrp', itemForm.mrp ? String(Number(itemForm.mrp)) : '');
      payload.append('unit', itemForm.unit.trim());
      payload.append(
        'stock_quantity',
        itemForm.stock_quantity === ''
          ? '0'
          : String(Math.max(0, Number(itemForm.stock_quantity) || 0))
      );
      payload.append('availability', itemForm.availability);

      if (itemImageFile) {
        payload.append('image', itemImageFile);
      }

      const isEdit = Boolean(editingShopItemId);

      const response = await fetch(
        isEdit
          ? `/api/cake/products/shop-items/${editingShopItemId}`
          : '/api/cake/products/shop-items',
        {
          method: isEdit ? 'PUT' : 'POST',
          headers: {
            Authorization: `Bearer ${token()}`,
          },
          body: payload,
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data.message ||
            (isEdit ? 'Failed to update item' : 'Failed to add item')
        );
      }

      setItemMessage({
        type: 'success',
        text: isEdit ? 'Item updated successfully' : 'Item added successfully',
      });

      await loadShopData(false);

      setTimeout(() => {
        closeItemForm();
      }, 500);
    } catch (error) {
      setItemMessage({
        type: 'error',
        text: error.message || 'Failed to save item',
      });
    } finally {
      setItemSaving(false);
    }
  };

  const editShopItem = (item) => {
    setEditingShopItemId(item.id);
    setItemForm({
      name: item.name || '',
      category_id: item.category_id ? String(item.category_id) : '',
      description: item.description || '',
      price: item.price ?? '',
      mrp: item.mrp ?? '',
      unit: item.unit || '',
      stock_quantity: item.stock_quantity ?? '',
      availability: item.availability || 'available',
    });
    setItemImageFile(null);
    setItemImagePreview(getShopItemImage(item.image));
    setItemMessage({ type: '', text: '' });
    setShowAddItem(true);
    setActiveTab('other');
  };

  const startEdit = (cake) => {
    setEditingId(cake.id);
    setMoreEditId(null);
    setRowMessage({ id: null, type: '', text: '' });

    setQuickEdit({
      name: cake.name || '',
      starting_price: cake.starting_price || '',
      availability: cake.availability || 'available',
    });

    setMoreEdit({
      category: cake.category || '',
      description: cake.description || '',
      egg_type: cake.egg_type || 'eggless',
      shape: cake.shape || '',
      cake_message_enabled: Boolean(cake.cake_message_enabled),
      customization_enabled: Boolean(cake.customization_enabled),
    });

    setEditVariants(
      Array.isArray(cake.variants) && cake.variants.length
        ? cake.variants.map((v) => ({
            weight: v.weight || '',
            price: v.price || '',
          }))
        : [{ weight: '', price: '' }]
    );

    setEditFlavors(
      Array.isArray(cake.flavors) && cake.flavors.length
        ? cake.flavors.map((f) =>
            typeof f === 'string' ? f : f.flavor_name || ''
          )
        : ['']
    );

    setEditImageFile(null);
    setEditImagePreview(getCakeImage(cake.image));
  };

  const cancelEdit = () => {
    setEditingId(null);
    setMoreEditId(null);
    setEditImageFile(null);
    setEditImagePreview('');
    setRowMessage({ id: null, type: '', text: '' });
  };

  const saveQuickEdit = async (cake) => {
    if (!quickEdit.name.trim()) {
      setRowMessage({
        id: cake.id,
        type: 'error',
        text: 'Cake name required',
      });
      return;
    }

    if (!quickEdit.starting_price || Number(quickEdit.starting_price) <= 0) {
      setRowMessage({
        id: cake.id,
        type: 'error',
        text: 'Valid price required',
      });
      return;
    }

    try {
      setSavingId(cake.id);
      setRowMessage({ id: null, type: '', text: '' });

      const formData = new FormData();
      formData.append('name', quickEdit.name.trim());
      formData.append('starting_price', String(Number(quickEdit.starting_price)));
      formData.append('availability', quickEdit.availability);

      if (editImageFile) {
        formData.append('image', editImageFile);
      }

      const response = await fetch(`/api/cake/products/${cake.id}/quick`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token()}`,
        },
        body: formData,
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to update cake');
      }

      setCakes((prev) =>
        prev.map((item) =>
          item.id === cake.id
            ? {
                ...item,
                name: quickEdit.name.trim(),
                starting_price: Number(quickEdit.starting_price),
                availability: quickEdit.availability,
                image: data.product?.image ?? item.image,
              }
            : item
        )
      );

      setRowMessage({
        id: cake.id,
        type: 'success',
        text: 'Saved',
      });

      setTimeout(() => {
        setEditingId(null);
        setMoreEditId(null);
        setRowMessage({ id: null, type: '', text: '' });
      }, 500);
    } catch (error) {
      setRowMessage({
        id: cake.id,
        type: 'error',
        text: error.message || 'Update failed',
      });
    } finally {
      setSavingId(null);
    }
  };

  const handleMoreEditChange = (e) => {
    const { name, value, type, checked } = e.target;
    setMoreEdit((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleEditImage = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setRowMessage({
        id: editingId,
        type: 'error',
        text: 'Please select an image',
      });
      return;
    }

    if (file.size > 3 * 1024 * 1024) {
      setRowMessage({
        id: editingId,
        type: 'error',
        text: 'Image must be under 3 MB',
      });
      return;
    }

    setEditImageFile(file);
    setEditImagePreview(URL.createObjectURL(file));
  };

  const updateVariant = (index, field, value) => {
    setEditVariants((prev) =>
      prev.map((v, i) => (i === index ? { ...v, [field]: value } : v))
    );
  };

  const addVariant = () =>
    setEditVariants((prev) => [...prev, { weight: '', price: '' }]);

  const removeVariant = (index) =>
    setEditVariants((prev) =>
      prev.length === 1 ? prev : prev.filter((_, i) => i !== index)
    );

  const updateFlavor = (index, value) => {
    setEditFlavors((prev) =>
      prev.map((f, i) => (i === index ? value : f))
    );
  };

  const addFlavor = () => setEditFlavors((prev) => [...prev, '']);

  const removeFlavor = (index) =>
    setEditFlavors((prev) =>
      prev.length === 1 ? prev : prev.filter((_, i) => i !== index)
    );

  const saveMoreEdit = async (cake) => {
    if (!quickEdit.name.trim()) {
      setRowMessage({ id: cake.id, type: 'error', text: 'Cake name required' });
      return;
    }

    try {
      setSavingId(cake.id);
      setRowMessage({ id: null, type: '', text: '' });

      const variants = editVariants
        .filter((v) => v.weight.trim() && v.price !== '')
        .map((v) => ({
          weight: v.weight.trim(),
          price: Number(v.price),
        }));

      const flavors = editFlavors.map((f) => f.trim()).filter(Boolean);

      const formData = new FormData();
      formData.append('name', quickEdit.name.trim());
      formData.append('starting_price', quickEdit.starting_price);
      formData.append('availability', quickEdit.availability);
      formData.append('category', moreEdit.category || '');
      formData.append('description', moreEdit.description || '');
      formData.append('egg_type', moreEdit.egg_type || 'eggless');
      formData.append('shape', moreEdit.shape || '');
      formData.append(
        'cake_message_enabled',
        moreEdit.cake_message_enabled ? 'true' : 'false'
      );
      formData.append(
        'customization_enabled',
        moreEdit.customization_enabled ? 'true' : 'false'
      );
      formData.append('variants', JSON.stringify(variants));
      formData.append('flavors', JSON.stringify(flavors));
      formData.append('existing_image', cake.image || '');

      if (editImageFile) {
        formData.append('image', editImageFile);
      }

      const response = await fetch(`/api/cake/products/${cake.id}`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token()}`,
        },
        body: formData,
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to update cake');
      }

      await loadCakes(false);

      setRowMessage({
        id: cake.id,
        type: 'success',
        text: 'All changes saved',
      });

      setTimeout(() => {
        setEditingId(null);
        setMoreEditId(null);
        setRowMessage({ id: null, type: '', text: '' });
      }, 600);
    } catch (error) {
      setRowMessage({
        id: cake.id,
        type: 'error',
        text: error.message || 'Update failed',
      });
    } finally {
      setSavingId(null);
    }
  };

  return (
    <div className="cake-products-page">
      <aside className="cake-products-sidebar">
        <div className="cake-products-brand">
          <div className="cake-products-logo">🎂</div>
          <div>
            <h2>VAKRA CAKE</h2>
            <span>Vendor Panel</span>
          </div>
        </div>

        <nav className="cake-products-menu">
          <button type="button" onClick={() => navigate('/cake/vendor')}>
            <span>🏠</span>Home
          </button>
          <button type="button" className="active">
            <span>🛍️</span>Products
          </button>
          <button
            type="button"
            onClick={() => navigate('/cake/orders')}
          >
            <span>📦</span>Orders
          </button>
          <button type="button"><span>👥</span>Customers</button>
          <button type="button"><span>🧾</span>Billing</button>
          <button type="button"><span>📊</span>Records</button>
          <button
            type="button"
            onClick={() => navigate('/cake/settings')}
          >
            <span>⚙️</span>Settings
          </button>
        </nav>

        <div className="cake-products-sidebar-bottom">
          <button
            type="button"
            className="cake-products-logout"
            onClick={handleLogout}
          >
            <span>↪</span>Logout
          </button>
        </div>
      </aside>

      <main className="cake-products-main">
        <header className="cake-products-topbar">
          <div className="cake-products-title">
            <h3>Products</h3>
            <p>Manage cakes, shop items, categories and offers.</p>
          </div>

          <div className="cake-products-profile">
            <div className="cake-products-profile-icon">
              {vendor.shop_name ? vendor.shop_name.charAt(0).toUpperCase() : 'C'}
            </div>
            <div className="cake-products-profile-info">
              <strong>{vendor.shop_name || 'Cake Shop'}</strong>
              <span>{vendor.vendor_code || 'VAKRA Vendor'}</span>
            </div>
          </div>
        </header>

        <section className="cake-products-content">
          <div className="cake-products-header">
            <div>
              <span className="cake-products-small-label">PRODUCT MANAGEMENT</span>
              <h1>My Products</h1>
              <p>Manage cakes and all other products available in your shop.</p>
            </div>

            <div className="cake-products-header-actions">
              <button
                type="button"
                className="cake-products-add-button secondary"
                onClick={openAddItem}
              >
                + Add Other Item
              </button>

              <button
                type="button"
                className="cake-products-add-button"
                onClick={() => navigate('/cake/cakes/add')}
              >
                + Add Cake
              </button>
            </div>
          </div>

          <div className="cake-products-controls">
            <div className="cake-products-search">
              <span>⌕</span>
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search cakes or other items..."
              />
            </div>
          </div>

          <div className="cake-products-tabs">
            <button
              type="button"
              className={activeTab === 'all' ? 'active' : ''}
              onClick={() => setActiveTab('all')}
            >
              Cakes
            </button>
            <button
              type="button"
              className={activeTab === 'other' ? 'active' : ''}
              onClick={() => setActiveTab('other')}
            >
              Other Items
            </button>
            <button
              type="button"
              className={activeTab === 'categories' ? 'active' : ''}
              onClick={() => setActiveTab('categories')}
            >
              Categories
            </button>
            <button
              type="button"
              className={activeTab === 'offers' ? 'active' : ''}
              onClick={() => setActiveTab('offers')}
            >
              Offers
            </button>
          </div>

          {activeTab === 'all' && (
            <div className="cake-products-panel">
              <div className="cake-products-table-heading">
                <div>
                  <h2>All Cakes</h2>
                  <p>Your complete cake product list.</p>
                </div>
                <span>{filteredCakes.length} Cakes</span>
              </div>

              {loading ? (
                <div className="cake-products-empty">
                  <div className="cake-products-empty-icon">🎂</div>
                  <h3>Loading Cakes...</h3>
                  <p>Please wait while your cake products are loading.</p>
                </div>
              ) : error ? (
                <div className="cake-products-empty">
                  <div className="cake-products-empty-icon">!</div>
                  <h3>Unable to Load Cakes</h3>
                  <p>{error}</p>
                  <button type="button" onClick={() => loadCakes()}>
                    Try Again
                  </button>
                </div>
              ) : filteredCakes.length === 0 ? (
                <div className="cake-products-empty">
                  <div className="cake-products-empty-icon">🎂</div>
                  <h3>{search.trim() ? 'No Cakes Found' : 'No Cakes Added Yet'}</h3>
                  <p>
                    {search.trim()
                      ? 'No cake matches your search.'
                      : 'Start by adding your first cake product.'}
                  </p>
                  {!search.trim() && (
                    <button
                      type="button"
                      onClick={() => navigate('/cake/cakes/add')}
                    >
                      + Add Your First Cake
                    </button>
                  )}
                </div>
              ) : (
                <div className="cake-products-table-wrapper">
                  <table className="cake-products-table">
                    <thead>
                      <tr>
                        <th>Image</th>
                        <th>Cake Name</th>
                        <th>Category</th>
                        <th>Starting Price</th>
                        <th>Variants</th>
                        <th>Availability</th>
                        <th>Offer</th>
                        <th>Edit</th>
                      </tr>
                    </thead>

                    <tbody>
                      {filteredCakes.map((cake) => {
                        const isEditing = editingId === cake.id;
                        const showMore = moreEditId === cake.id;

                        return (
                          <React.Fragment key={cake.id}>
                            <tr className={isEditing ? 'cake-row-editing' : ''}>
                              <td>
                                {isEditing ? (
                                  <label className="cake-quick-image-edit">
                                    <div className="cake-product-image">
                                      {editImagePreview ? (
                                        <img
                                          src={editImagePreview}
                                          alt={quickEdit.name || cake.name}
                                        />
                                      ) : (
                                        '🎂'
                                      )}
                                    </div>
                                    <span className="cake-quick-image-badge">📷</span>
                                    <input
                                      type="file"
                                      accept="image/jpeg,image/png,image/webp"
                                      onChange={handleEditImage}
                                    />
                                  </label>
                                ) : (
                                  <button
                                    type="button"
                                    className="cake-product-image-button"
                                    onClick={() =>
                                      cake.image && setPreviewImage(getCakeImage(cake.image))
                                    }
                                    disabled={!cake.image}
                                    aria-label={cake.image ? `View ${cake.name} image` : 'No cake image'}
                                  >
                                    <div className="cake-product-image">
                                      {cake.image ? (
                                        <img
                                          src={getCakeImage(cake.image)}
                                          alt={cake.name}
                                        />
                                      ) : (
                                        '🎂'
                                      )}
                                    </div>
                                  </button>
                                )}
                              </td>

                              <td>
                                {isEditing ? (
                                  <input
                                    className="cake-inline-input cake-inline-name"
                                    value={quickEdit.name}
                                    onChange={(e) =>
                                      setQuickEdit((prev) => ({
                                        ...prev,
                                        name: e.target.value,
                                      }))
                                    }
                                  />
                                ) : (
                                  <strong>{cake.name}</strong>
                                )}
                              </td>

                              <td>{cake.category || '-'}</td>

                              <td>
                                {isEditing ? (
                                  <div className="cake-inline-price">
                                    <span>₹</span>
                                    <input
                                      className="cake-inline-input"
                                      type="number"
                                      min="0"
                                      value={quickEdit.starting_price}
                                      onChange={(e) =>
                                        setQuickEdit((prev) => ({
                                          ...prev,
                                          starting_price: e.target.value,
                                        }))
                                      }
                                    />
                                  </div>
                                ) : (
                                  <>₹{Number(cake.starting_price || 0).toLocaleString('en-IN')}</>
                                )}
                              </td>

                              <td>
                                {cake.variant_count ?? cake.variants?.length ?? 0}
                              </td>

                              <td>
                                {isEditing ? (
                                  <select
                                    className="cake-inline-select"
                                    value={quickEdit.availability}
                                    onChange={(e) =>
                                      setQuickEdit((prev) => ({
                                        ...prev,
                                        availability: e.target.value,
                                      }))
                                    }
                                  >
                                    <option value="available">Available</option>
                                    <option value="out_of_stock">Out of Stock</option>
                                  </select>
                                ) : (
                                  <span
                                    className={
                                      cake.availability === 'available'
                                        ? 'cake-available'
                                        : 'cake-unavailable'
                                    }
                                  >
                                    {cake.availability === 'available'
                                      ? 'Available'
                                      : 'Out of Stock'}
                                  </span>
                                )}
                              </td>

                              <td>
                                {cake.offer ? (
                                  <span className="cake-offer-badge">{cake.offer}</span>
                                ) : (
                                  <span className="cake-no-offer">No Offer</span>
                                )}
                              </td>

                              <td>
                                {!isEditing ? (
                                  <button
                                    type="button"
                                    className="cake-product-edit-button"
                                    onClick={() => startEdit(cake)}
                                  >
                                    ✎
                                  </button>
                                ) : (
                                  <div className="cake-inline-actions">
                                    <button
                                      type="button"
                                      className="cake-inline-save"
                                      disabled={savingId === cake.id}
                                      onClick={() => saveQuickEdit(cake)}
                                    >
                                      {savingId === cake.id ? '...' : 'Save'}
                                    </button>

                                    <button
                                      type="button"
                                      className="cake-inline-more"
                                      onClick={() =>
                                        setMoreEditId(showMore ? null : cake.id)
                                      }
                                    >
                                      {showMore ? 'Less' : 'More Edit'}
                                    </button>

                                    <button
                                      type="button"
                                      className="cake-inline-cancel"
                                      onClick={cancelEdit}
                                    >
                                      ×
                                    </button>
                                  </div>
                                )}
                              </td>
                            </tr>

                            {isEditing && rowMessage.id === cake.id && (
                              <tr className="cake-inline-message-row">
                                <td colSpan="8">
                                  <div className={`cake-inline-message ${rowMessage.type}`}>
                                    {rowMessage.text}
                                  </div>
                                </td>
                              </tr>
                            )}

                            {isEditing && showMore && (
                              <tr className="cake-more-edit-row">
                                <td colSpan="8">
                                  <div className="cake-more-edit-box">
                                    <div className="cake-more-edit-title">
                                      <div>
                                        <strong>More Edit</strong>
                                        <span>Detailed cake settings</span>
                                      </div>
                                    </div>

                                    <div className="cake-more-grid">
                                      <div className="cake-more-field">
                                        <label>Category</label>
                                        <select
                                          name="category"
                                          value={moreEdit.category}
                                          onChange={handleMoreEditChange}
                                        >
                                          <option value="">Select Category</option>
                                          {categories.map((category) => (
                                            <option key={category} value={category}>
                                              {category}
                                            </option>
                                          ))}
                                        </select>
                                      </div>

                                      <div className="cake-more-field">
                                        <label>Egg Type</label>
                                        <select
                                          name="egg_type"
                                          value={moreEdit.egg_type}
                                          onChange={handleMoreEditChange}
                                        >
                                          <option value="eggless">Eggless</option>
                                          <option value="egg">With Egg</option>
                                          <option value="both">Both</option>
                                        </select>
                                      </div>

                                      <div className="cake-more-field">
                                        <label>Shape</label>
                                        <select
                                          name="shape"
                                          value={moreEdit.shape}
                                          onChange={handleMoreEditChange}
                                        >
                                          {shapes.map((shape) => (
                                            <option key={shape.value} value={shape.value}>
                                              {shape.label}
                                            </option>
                                          ))}
                                        </select>
                                      </div>

                                      <div className="cake-more-field cake-more-full">
                                        <label>Description</label>
                                        <textarea
                                          name="description"
                                          rows="3"
                                          value={moreEdit.description}
                                          onChange={handleMoreEditChange}
                                        />
                                      </div>
                                    </div>

                                    <div className="cake-more-options">
                                      <label>
                                        <span>Cake Message</span>
                                        <input
                                          type="checkbox"
                                          name="cake_message_enabled"
                                          checked={moreEdit.cake_message_enabled}
                                          onChange={handleMoreEditChange}
                                        />
                                      </label>

                                      <label>
                                        <span>Customization</span>
                                        <input
                                          type="checkbox"
                                          name="customization_enabled"
                                          checked={moreEdit.customization_enabled}
                                          onChange={handleMoreEditChange}
                                        />
                                      </label>
                                    </div>

                                    <div className="cake-more-image-section">
                                      <div className="cake-more-image-preview">
                                        {editImagePreview ? (
                                          <img src={editImagePreview} alt="Cake preview" />
                                        ) : (
                                          '🎂'
                                        )}
                                      </div>

                                      <label className="cake-more-image-button">
                                        Change Image
                                        <input
                                          type="file"
                                          accept="image/*"
                                          onChange={handleEditImage}
                                        />
                                      </label>
                                    </div>

                                    <div className="cake-more-subsection">
                                      <div className="cake-more-subtitle">
                                        <strong>Weight & Price Variants</strong>
                                        <button type="button" onClick={addVariant}>
                                          + Add Variant
                                        </button>
                                      </div>

                                      <div className="cake-more-list">
                                        {editVariants.map((variant, index) => (
                                          <div className="cake-more-list-row" key={index}>
                                            <input
                                              type="text"
                                              placeholder="500 gm"
                                              value={variant.weight}
                                              onChange={(e) =>
                                                updateVariant(index, 'weight', e.target.value)
                                              }
                                            />
                                            <input
                                              type="number"
                                              min="0"
                                              placeholder="499"
                                              value={variant.price}
                                              onChange={(e) =>
                                                updateVariant(index, 'price', e.target.value)
                                              }
                                            />
                                            <button
                                              type="button"
                                              disabled={editVariants.length === 1}
                                              onClick={() => removeVariant(index)}
                                            >
                                              ×
                                            </button>
                                          </div>
                                        ))}
                                      </div>
                                    </div>

                                    <div className="cake-more-subsection">
                                      <div className="cake-more-subtitle">
                                        <strong>Flavors</strong>
                                        <button type="button" onClick={addFlavor}>
                                          + Add Flavor
                                        </button>
                                      </div>

                                      <div className="cake-more-list">
                                        {editFlavors.map((flavor, index) => (
                                          <div
                                            className="cake-more-list-row cake-more-flavor-row"
                                            key={index}
                                          >
                                            <input
                                              type="text"
                                              placeholder="Chocolate"
                                              value={flavor}
                                              onChange={(e) =>
                                                updateFlavor(index, e.target.value)
                                              }
                                            />
                                            <button
                                              type="button"
                                              disabled={editFlavors.length === 1}
                                              onClick={() => removeFlavor(index)}
                                            >
                                              ×
                                            </button>
                                          </div>
                                        ))}
                                      </div>
                                    </div>

                                    <div className="cake-more-actions">
                                      <button
                                        type="button"
                                        className="cake-more-cancel"
                                        onClick={() => setMoreEditId(null)}
                                      >
                                        Close More Edit
                                      </button>
                                      <button
                                        type="button"
                                        className="cake-more-save"
                                        disabled={savingId === cake.id}
                                        onClick={() => saveMoreEdit(cake)}
                                      >
                                        {savingId === cake.id
                                          ? 'Saving...'
                                          : 'Save All Changes'}
                                      </button>
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {activeTab === 'other' && (
            <>
              {showAddItem && (
                <div className="cake-products-panel cake-shop-form-panel">
                  <div className="cake-products-table-heading">
                    <div>
                      <h2>
                        {editingShopItemId ? 'Edit Other Item' : 'Add Other Item'}
                      </h2>
                      <p>
                        Add namkeen, balloons, bakery products, decorations or any other shop item.
                      </p>
                    </div>
                    <button
                      type="button"
                      className="cake-shop-close-button"
                      onClick={closeItemForm}
                    >
                      ×
                    </button>
                  </div>

                  {itemMessage.text && (
                    <div className={`cake-shop-message ${itemMessage.type}`}>
                      {itemMessage.text}
                    </div>
                  )}

                  <form className="cake-shop-item-form" onSubmit={saveShopItem}>
                    <div className="cake-shop-form-grid">
                      <div className="cake-shop-field cake-shop-full">
                        <label>Product Name *</label>
                        <input
                          type="text"
                          name="name"
                          value={itemForm.name}
                          onChange={handleItemChange}
                          placeholder="Example: Happy Birthday Balloon Pack"
                        />
                      </div>

                      <div className="cake-shop-field">
                        <label>Category *</label>
                        <div className="cake-shop-category-control">
                          <select
                            name="category_id"
                            value={itemForm.category_id}
                            onChange={handleItemChange}
                          >
                            <option value="">
                              {shopCategories.filter((c) => c.status === 'active').length
                                ? 'Select Category'
                                : 'No Category Added'}
                            </option>
                            {shopCategories
                              .filter((category) => category.status === 'active')
                              .map((category) => (
                                <option key={category.id} value={category.id}>
                                  {category.category_name}
                                </option>
                              ))}
                          </select>

                          <button
                            type="button"
                            onClick={() => setShowAddCategory(true)}
                          >
                            + Add Category
                          </button>
                        </div>
                      </div>

                      <div className="cake-shop-field">
                        <label>Price *</label>
                        <div className="cake-inline-price">
                          <span>₹</span>
                          <input
                            type="number"
                            min="0"
                            name="price"
                            value={itemForm.price}
                            onChange={handleItemChange}
                            placeholder="100"
                          />
                        </div>
                      </div>

                      <div className="cake-shop-field">
                        <label>MRP</label>
                        <div className="cake-inline-price">
                          <span>₹</span>
                          <input
                            type="number"
                            min="0"
                            name="mrp"
                            value={itemForm.mrp}
                            onChange={handleItemChange}
                            placeholder="120"
                          />
                        </div>
                      </div>

                      <div className="cake-shop-field">
                        <label>Unit / Size</label>
                        <input
                          type="text"
                          name="unit"
                          value={itemForm.unit}
                          onChange={handleItemChange}
                          placeholder="500 gm / 1 Pack / 10 Pcs"
                        />
                      </div>

                      <div className="cake-shop-field">
                        <label>Stock Quantity</label>
                        <input
                          type="number"
                          min="0"
                          name="stock_quantity"
                          value={itemForm.stock_quantity}
                          onChange={handleItemChange}
                          placeholder="20"
                        />
                      </div>

                      <div className="cake-shop-field">
                        <label>Availability</label>
                        <select
                          name="availability"
                          value={itemForm.availability}
                          onChange={handleItemChange}
                        >
                          <option value="available">Available</option>
                          <option value="out_of_stock">Out of Stock</option>
                        </select>
                      </div>

                      <div className="cake-shop-field cake-shop-full">
                        <label>Description</label>
                        <textarea
                          name="description"
                          rows="3"
                          value={itemForm.description}
                          onChange={handleItemChange}
                          placeholder="Short description about this product..."
                        />
                      </div>

                      <div className="cake-shop-field cake-shop-full">
                        <label>Product Image</label>
                        <div className="cake-shop-image-row">
                          <div className="cake-shop-image-preview">
                            {itemImagePreview ? (
                              <img src={itemImagePreview} alt="Item preview" />
                            ) : (
                              <span>🛍️</span>
                            )}
                          </div>

                          <label className="cake-shop-upload-button">
                            {itemImagePreview ? 'Change Image' : 'Upload Image'}
                            <input
                              type="file"
                              accept="image/jpeg,image/png,image/webp"
                              onChange={handleItemImage}
                            />
                          </label>
                        </div>
                      </div>
                    </div>

                    <div className="cake-shop-form-actions">
                      <button
                        type="button"
                        className="cake-shop-cancel-button"
                        onClick={closeItemForm}
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="cake-shop-save-button"
                        disabled={itemSaving}
                      >
                        {itemSaving
                          ? 'Saving...'
                          : editingShopItemId
                          ? 'Update Item'
                          : 'Save Item'}
                      </button>
                    </div>
                  </form>
                </div>
              )}

              <div className="cake-products-panel">
                <div className="cake-products-table-heading">
                  <div>
                    <h2>Other Items</h2>
                    <p>All non-cake products available in your shop.</p>
                  </div>
                  <span>{filteredShopItems.length} Items</span>
                </div>

                {shopLoading ? (
                  <div className="cake-products-empty">
                    <div className="cake-products-empty-icon">🛍️</div>
                    <h3>Loading Items...</h3>
                  </div>
                ) : shopError ? (
                  <div className="cake-products-empty">
                    <div className="cake-products-empty-icon">!</div>
                    <h3>Unable to Load Items</h3>
                    <p>{shopError}</p>
                    <button type="button" onClick={() => loadShopData()}>
                      Try Again
                    </button>
                  </div>
                ) : filteredShopItems.length === 0 ? (
                  <div className="cake-products-empty">
                    <div className="cake-products-empty-icon">🛍️</div>
                    <h3>No Other Items Added Yet</h3>
                    <p>Add any non-cake product sold by this shop.</p>
                    <button type="button" onClick={openAddItem}>
                      + Add Other Item
                    </button>
                  </div>
                ) : (
                  <div className="cake-products-table-wrapper">
                    <table className="cake-products-table cake-shop-items-table">
                      <thead>
                        <tr>
                          <th>Image</th>
                          <th>Product</th>
                          <th>Category</th>
                          <th>Price</th>
                          <th>Unit</th>
                          <th>Stock</th>
                          <th>Availability</th>
                          <th>Edit</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredShopItems.map((item) => (
                          <tr key={item.id}>
                            <td>
                              <button
                                type="button"
                                className="cake-product-image-button"
                                onClick={() =>
                                  item.image &&
                                  setPreviewImage(getShopItemImage(item.image))
                                }
                                disabled={!item.image}
                              >
                                <div className="cake-product-image">
                                  {item.image ? (
                                    <img
                                      src={getShopItemImage(item.image)}
                                      alt={item.name}
                                    />
                                  ) : (
                                    '🛍️'
                                  )}
                                </div>
                              </button>
                            </td>
                            <td>
                              <strong>{item.name}</strong>
                            </td>
                            <td>{item.category_name || '-'}</td>
                            <td>₹{Number(item.price || 0).toLocaleString('en-IN')}</td>
                            <td>{item.unit || '-'}</td>
                            <td>{item.stock_quantity ?? 0}</td>
                            <td>
                              <span
                                className={
                                  item.availability === 'available'
                                    ? 'cake-available'
                                    : 'cake-unavailable'
                                }
                              >
                                {item.availability === 'available'
                                  ? 'Available'
                                  : 'Out of Stock'}
                              </span>
                            </td>
                            <td>
                              <button
                                type="button"
                                className="cake-product-edit-button"
                                onClick={() => editShopItem(item)}
                              >
                                ✎
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          )}

          {activeTab === 'categories' && (
            <div className="cake-products-panel">
              <div className="cake-products-table-heading">
                <div>
                  <h2>Other Item Categories</h2>
                  <p>Vendor can create categories according to the products sold in this shop.</p>
                </div>
                <button
                  type="button"
                  className="cake-create-offer-button"
                  onClick={() => setShowAddCategory(true)}
                >
                  + Add Category
                </button>
              </div>

              {itemMessage.text && activeTab === 'categories' && (
                <div className={`cake-shop-message ${itemMessage.type}`}>
                  {itemMessage.text}
                </div>
              )}

              {shopCategories.length === 0 ? (
                <div className="cake-products-empty">
                  <div className="cake-products-empty-icon">🏷️</div>
                  <h3>No Categories Added Yet</h3>
                  <p>Create your own category. No dummy categories will be added.</p>
                  <button
                    type="button"
                    onClick={() => setShowAddCategory(true)}
                  >
                    + Add Category
                  </button>
                </div>
              ) : (
                <div className="cake-shop-category-list">
                  {shopCategories.map((category) => (
                    <div className="cake-shop-category-row" key={category.id}>
                      <div className="cake-shop-category-name">
                        <span>🏷️</span>
                        {categoryEditingId === category.id ? (
                          <input
                            type="text"
                            value={categoryEditingName}
                            onChange={(e) =>
                              setCategoryEditingName(e.target.value)
                            }
                          />
                        ) : (
                          <div>
                            <strong>{category.category_name}</strong>
                            <small>
                              {category.item_count ?? 0}{' '}
                              {(category.item_count ?? 0) === 1 ? 'Item' : 'Items'}
                            </small>
                          </div>
                        )}
                      </div>

                      <span
                        className={
                          category.status === 'active'
                            ? 'cake-available'
                            : 'cake-unavailable'
                        }
                      >
                        {category.status === 'active' ? 'Active' : 'Inactive'}
                      </span>

                      <div className="cake-shop-category-actions">
                        {categoryEditingId === category.id ? (
                          <>
                            <button
                              type="button"
                              className="save"
                              disabled={categorySaving}
                              onClick={() => saveCategoryEdit(category.id)}
                            >
                              Save
                            </button>
                            <button
                              type="button"
                              onClick={cancelCategoryEdit}
                            >
                              Cancel
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              type="button"
                              onClick={() => startCategoryEdit(category)}
                            >
                              Edit
                            </button>
                            <button
                              type="button"
                              onClick={() => toggleCategoryStatus(category)}
                            >
                              {category.status === 'active'
                                ? 'Disable'
                                : 'Activate'}
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'offers' && (
            <div className="cake-products-panel">
              <div className="cake-products-table-heading">
                <div>
                  <h2>Cake Offers</h2>
                  <p>Manage special offers available on your cakes.</p>
                </div>
                <button type="button" className="cake-create-offer-button">
                  + Create Offer
                </button>
              </div>

              <div className="cake-products-empty">
                <div className="cake-products-empty-icon offer">%</div>
                <h3>No Active Offers</h3>
                <p>Create offers for selected cakes when required.</p>
              </div>
            </div>
          )}

          <div className="cake-products-info-grid">
            <div className="cake-products-info-card">
              <div className="cake-products-info-icon">🎂</div>
              <div>
                <strong>Product Details</strong>
                <p>Use quick edit for daily changes and More Edit for full details.</p>
              </div>
            </div>

            <div className="cake-products-info-card">
              <div className="cake-products-info-icon">🏷️</div>
              <div>
                <strong>Offers</strong>
                <p>Categories and other items stay inside Products, so the sidebar remains simple.</p>
              </div>
            </div>
          </div>
        </section>
      </main>

      {showAddCategory && (
        <div
          className="cake-shop-modal-overlay"
          onClick={() => {
            if (!categorySaving) {
              setShowAddCategory(false);
              setCategoryName('');
            }
          }}
          role="presentation"
        >
          <div
            className="cake-shop-category-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="cake-shop-modal-heading">
              <div>
                <h3>Add Category</h3>
                <p>Create your own category for other shop items.</p>
              </div>
              <button
                type="button"
                onClick={() => {
                  setShowAddCategory(false);
                  setCategoryName('');
                }}
              >
                ×
              </button>
            </div>

            <label>Category Name</label>
            <input
              type="text"
              value={categoryName}
              onChange={(e) => setCategoryName(e.target.value)}
              placeholder="Enter category name"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  saveCategory();
                }
              }}
            />

            <div className="cake-shop-modal-actions">
              <button
                type="button"
                className="cake-shop-cancel-button"
                onClick={() => {
                  setShowAddCategory(false);
                  setCategoryName('');
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="cake-shop-save-button"
                disabled={categorySaving || !categoryName.trim()}
                onClick={saveCategory}
              >
                {categorySaving ? 'Adding...' : 'Add Category'}
              </button>
            </div>
          </div>
        </div>
      )}

      {previewImage && (
        <div
          className="cake-image-preview-overlay"
          onClick={() => setPreviewImage('')}
          role="presentation"
        >
          <div
            className="cake-image-preview-box"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="cake-image-preview-close"
              onClick={() => setPreviewImage('')}
              aria-label="Close image preview"
            >
              ×
            </button>
            <img src={previewImage} alt="Cake preview" />
          </div>
        </div>
      )}
    </div>
  );
};

export default CakeProducts;
